import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button } from "../components/ui";
import { ConfirmModal } from "../components/ui/Modal";
import ExportButton from "../components/ui/ExportButton";
import { exportConfigs } from "../utils/export";

function FornitoriListPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tipologiaFilter, setTipologiaFilter] = useState("");
  const [tipologie, setTipologie] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [exportData, setExportData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Controlli permessi
  const isAdmin = user?.ruolo === 100000000;
  const canExport = [100000000, 100000001, 100000002, 100000003].includes(
    user?.ruolo,
  );

  const MACROCATEGORIE = {
    100000000: "Manutenzione",
    100000001: "Servizi",
    100000002: "Forniture",
    100000003: "Consulenza",
  };

  useEffect(() => {
    // Fetch tipologie for filter dropdown
    const fetchTipologie = async () => {
      try {
        const result = await apiRequest("/tipologie-fornitura");
        setTipologie(result.value || []);
      } catch (error) {
        console.error("Error fetching tipologie:", error);
      }
    };
    fetchTipologie();
  }, [apiRequest]);

  useEffect(() => {
    const fetchFornitori = async () => {
      try {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (tipologiaFilter) params.append("tipologiaId", tipologiaFilter);

        const result = await apiRequest(`/fornitori?${params.toString()}`);
        setFornitori(result.value || []);
        setExportData([]); // Reset export when filters change
      } catch (error) {
        console.error("Error fetching fornitori:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchFornitori, 300);
    return () => clearTimeout(debounce);
  }, [apiRequest, search, tipologiaFilter]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (tipologiaFilter) params.append("tipologiaId", tipologiaFilter);
      const result = await apiRequest(`/fornitori?${params.toString()}`);
      setFornitori(result.value || []);
    } catch (error) {
      console.error("Error fetching fornitori:", error);
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      await apiRequest(`/fornitori/${itemToDelete.id}`, { method: "DELETE" });
      showToast("Fornitore eliminato con successo", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting fornitore:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportClick = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const result = await apiRequest(`/fornitori/export?${params.toString()}`);
      setExportData(result.value || []);
    } catch (error) {
      console.error("Error fetching export data:", error);
      showToast("Errore durante il caricamento dei dati per l'export", "error");
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Fornitori</h1>
        {canExport && (
          <div className="flex items-center gap-3">
            {exportData.length === 0 ? (
              <button
                onClick={handleExportClick}
                disabled={exportLoading || fornitori.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${
                  exportLoading || fornitori.length === 0
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {exportLoading ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                )}
                {exportLoading ? "Caricamento..." : "Prepara Export"}
              </button>
            ) : (
              <ExportButton
                data={exportData}
                columns={exportConfigs.fornitori}
                filename="fornitori"
              />
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 flex-wrap">
          <input
            type="text"
            placeholder="Cerca fornitore..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1 min-w-[200px]"
          />
          <select
            value={tipologiaFilter}
            onChange={(e) => setTipologiaFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">Tutte le tipologie</option>
            {tipologie.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {fornitori.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>P.IVA</th>
                <th>Email</th>
                <th>Telefono</th>
                <th>Tipologia</th>
                <th>Utenza Portale</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fornitori.map((f) => {
                const up = f.utentePortale;
                let badgeClass, badgeLabel, badgeTitle;
                if (!up) {
                  badgeClass =
                    "bg-gray-100 text-gray-500 border border-gray-300";
                  badgeLabel = "Nessuna";
                  badgeTitle = "Nessuna utenza collegata al portale";
                } else if (!up.attivo) {
                  badgeClass = "bg-red-50 text-red-700 border border-red-200";
                  badgeLabel = "Disattivata";
                  badgeTitle = `Utenza disattivata (${up.email})`;
                } else if (!up.haAccesso) {
                  badgeClass =
                    "bg-amber-50 text-amber-700 border border-amber-200";
                  badgeLabel = "Invitato";
                  badgeTitle = `Invito inviato, mai effettuato accesso (${up.email})`;
                } else {
                  badgeClass =
                    "bg-green-50 text-green-700 border border-green-200";
                  badgeLabel = "Attiva";
                  badgeTitle = `Utenza attiva (${up.email})`;
                }
                return (
                  <tr key={f.id}>
                    <td className="font-medium">{f.nome || "-"}</td>
                    <td>{f.partitaIva || "-"}</td>
                    <td>{f.emailPrincipale || "-"}</td>
                    <td>{f.telefono || "-"}</td>
                    <td>
                      {MACROCATEGORIE[f.macrocategoria] ||
                        f.macrocategoria ||
                        "-"}
                    </td>
                    <td>
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}
                        title={badgeTitle}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            !up
                              ? "bg-gray-400"
                              : !up.attivo
                                ? "bg-red-500"
                                : !up.haAccesso
                                  ? "bg-amber-500"
                                  : "bg-green-500"
                          }`}
                        />
                        {badgeLabel}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <Link
                          to={`/fornitori/${f.id}`}
                          className="text-primary-600 hover:underline"
                        >
                          Dettagli
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => confirmDelete(f)}
                            className="text-red-600 hover:text-red-800"
                            title="Elimina"
                          >
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 text-center py-8">
            Nessun fornitore trovato
          </p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Fornitore"
        message={`Sei sicuro di voler eliminare il fornitore "${itemToDelete?.nome}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

export default FornitoriListPage;
